import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, CircularProgress, Alert,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { fundTypeApi } from '../../services/api';
import { FundType } from '../../types';

const FundTypeList: React.FC = () => {
  const [fundTypes, setFundTypes] = useState<FundType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FundType | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fundTypeApi.getAll();
      setFundTypes(Array.isArray(res.data) ? res.data : []);
    } catch { setError('Failed to load fund types.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => {
    setEditingItem(null); setName(''); setDesc(''); setFormError(''); setDialogOpen(true);
  };

  const openEdit = (ft: FundType) => {
    setEditingItem(ft); setName(ft.name); setDesc(ft.description || ''); setFormError(''); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { setFormError('Name is required.'); return; }
    setSaving(true); setFormError('');
    try {
      const payload = { name: name.trim(), description: desc.trim() };
      if (editingItem) await fundTypeApi.update(editingItem.id, payload);
      else await fundTypeApi.create(payload);
      await fetchData();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this fund type?')) return;
    try {
      await fundTypeApi.delete(id);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Fund Types</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Fund Type</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fundTypes.length === 0 ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No fund types.</Typography></TableCell></TableRow>
              ) : fundTypes.map(ft => (
                <TableRow key={ft.id} hover>
                  <TableCell><Typography sx={{ fontWeight: 600 }}>{ft.name}</Typography></TableCell>
                  <TableCell>{ft.description || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(ft)} color="primary"><Edit /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(ft.id)} color="error"><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Fund Type' : 'Add Fund Type'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Name" fullWidth margin="normal" value={name} onChange={e => setName(e.target.value)} required disabled={saving} />
          <TextField label="Description" fullWidth margin="normal" value={desc} onChange={e => setDesc(e.target.value)} multiline rows={2} disabled={saving} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundTypeList;
